import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsAPI, usersAPI, clientsAPI, locationsAPI, projectsAPI } from '../../api/client';
import { isAvailableLocation, isClientCompatible, locationClientLabel, loadLocationOptions, matchesLocationSearch } from '../../lib/locationSelection';
import { changePrincipal } from '../../lib/traceability';
import TeamSelector from '../../components/traceability/TeamSelector';
import { EVENT_TYPES, RISK_LEVEL } from '../../lib/eventConstants';
import { ArrowLeft, Save, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';

const AGE_RANGES = ['Menos de 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const AUDIENCE_TYPES = [
  { id: 'consumo_alcohol', label: 'Consumo de alcohol' },
  { id: 'familiares', label: 'Familiares' },
  { id: 'corporativos', label: 'Corporativos' },
  { id: 'prensa', label: 'Prensa' },
  { id: 'vip', label: 'VIP' },
];

function SectionTitle({ n, children }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-6 h-6 bg-soem-primary text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{n}</div>
      <h3 className="font-bold text-soem-navy">{children}</h3>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function ToggleBtns({ options, value, onChange, getStyle }) {
  return (
    <div className="flex gap-2">
      {options.map(opt => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
          className={`flex-1 py-3 rounded-sm border-2 font-bold text-sm transition-all ${value === opt.value ? `${opt.style} text-white border-transparent` : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function EventForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [locationNotice, setLocationNotice] = useState('');
  const [locationRetry, setLocationRetry] = useState(0);
  const [searchingLoc, setSearchingLoc] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '', client_id: '', project_id: '', event_type: 'concierto', risk_level: 'medio',
    dates: { setup_start: '', doors_open: '', event_start: '', event_end: '', teardown_date: '' },
    location_id: '',
    expected_capacity: '',
    crowd_management: 'parados',
    age_ranges: [],
    audience_types: [],
    assigned_leader_id: '',
    assigned_levantador_id: '',
    assigned_user_ids: [],
    notes: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setDate = (k, v) => setForm(f => ({ ...f, dates: { ...f.dates, [k]: v } }));

  // #AutoCalcDates — al fijar la fecha de inicio de montaje, pre-cargar
  // el resto del cronograma:
  //   apertura de puertas = montaje +2 días
  //   inicio del evento   = montaje +3 días
  //   fin del evento      = montaje +4 días
  //   desmontaje          = montaje +6 días
  // Sólo se llenan los slots que el usuario NO haya editado (vacíos), así
  // si después cambia manualmente algo, no se sobreescribe. Útil sobre
  // todo en /events/new — en modo edición ya hay fechas guardadas.
  const setSetupStartAndDerive = (val) => {
    setForm((f) => {
      const newDates = { ...f.dates, setup_start: val };
      if (val) {
        const base = new Date(val);
        if (!isNaN(base.getTime())) {
          // Formatea Date → "YYYY-MM-DDTHH:mm" (formato datetime-local).
          const fmt = (d) => {
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          };
          const addDays = (n) => {
            const d = new Date(base);
            d.setDate(d.getDate() + n);
            return fmt(d);
          };
          const offsets = { doors_open: 2, event_start: 3, event_end: 4, teardown_date: 6 };
          for (const [key, days] of Object.entries(offsets)) {
            if (!newDates[key]) newDates[key] = addDays(days);
          }
        }
      }
      return { ...f, dates: newDates };
    });
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  useEffect(() => {
    Promise.all([clientsAPI.list(), usersAPI.list({ limit: 500 }), projectsAPI.list()]).then(([c, u, p]) => {
      setClients(c.data.items || []);
      setUsers(u.data.items || []);
      setProjects(p.data.items || []);
    }).catch(() => {});

    if (isEdit) {
      eventsAPI.get(id).then(r => {
        const e = r.data;
        setForm({
          name: e.name || '',
          client_id: e.client_id || '',
          project_id: e.project_id || '',
          event_type: e.event_type || 'concierto',
          risk_level: e.projected_risk_level || 'medio',
          dates: {
            setup_start: e.dates?.setup_start?.slice(0, 16) || '',
            doors_open: e.dates?.doors_open?.slice(0, 16) || '',
            event_start: e.dates?.event_start?.slice(0, 16) || '',
            event_end: e.dates?.event_end?.slice(0, 16) || '',
            teardown_date: e.dates?.teardown_date?.slice(0, 16) || '',
          },
          location_id: e.location_id || '',
          expected_capacity: e.expected_attendance || '',
          crowd_management: e.crowd_management || 'parados',
          age_ranges: e.audience_profile?.age_ranges || [],
          audience_types: e.audience_profile?.audience_types || [],
          assigned_leader_id: e.assigned_leader_id || '',
          assigned_levantador_id: e.assigned_levantador_id || '',
          assigned_user_ids: e.assigned_user_ids || [],
          notes: e.notes || '',
        });
        if (e.location) setSelectedLocation(e.location);
      }).catch(() => toast.error('Error al cargar evento'));
    }
  }, [id, isEdit]);

  useEffect(() => {
    let current = true;
    setLocationResults([]);
    setLocationError('');
    if (locationSearch.length < 2) { setSearchingLoc(false); return; }
    setSearchingLoc(true);
    const timer = setTimeout(() => {
      loadLocationOptions(locationsAPI.list, () => current).then(items => {
        if (current) setLocationResults(items.filter(loc =>
          isAvailableLocation(loc, form.client_id) && matchesLocationSearch(loc, locationSearch)));
      }).catch(() => {
        if (current) setLocationError('No se pudieron cargar las locaciones');
      }).finally(() => { if (current) setSearchingLoc(false); });
    }, 300);
    return () => { current = false; clearTimeout(timer); };
  }, [locationSearch, form.client_id, locationRetry]);

  const changeClient = (clientId) => {
    if (clientId === form.client_id) return;
    setLocationNotice(form.location_id && (!selectedLocation || !isClientCompatible(selectedLocation, clientId))
      ? 'Se quitó la locación al cambiar de cliente; elige una locación compatible.' : '');
    setForm(f => ({ ...f, client_id: clientId,
      project_id: projects.find(p => p.id === f.project_id)?.client_id === clientId ? f.project_id : '',
      location_id: selectedLocation && isClientCompatible(selectedLocation, clientId) ? f.location_id : '',
    }));
    if (!selectedLocation || !isClientCompatible(selectedLocation, clientId)) setSelectedLocation(null);
    setLocationSearch('');
    setLocationResults([]);
  };

  const toggleArr = (arr, item) => arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];

  const validateDates = () => {
    const { event_start, event_end, setup_start, doors_open } = form.dates;
    if (event_start && event_end && new Date(event_start) >= new Date(event_end))
      return 'La fecha de inicio debe ser anterior a la fecha de fin.';
    if (doors_open && event_start && new Date(doors_open) > new Date(event_start))
      return 'La apertura de puertas debe ser antes del inicio.';
    if (setup_start && doors_open && new Date(setup_start) > new Date(doors_open))
      return 'El montaje debe comenzar antes de la apertura de puertas.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateErr = validateDates();
    if (dateErr) { toast.error(dateErr); return; }
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    if (!form.dates.event_start) { toast.error('La fecha de inicio es requerida'); return; }
    if (!form.dates.event_end) { toast.error('La fecha de fin es requerida'); return; }
    if (!form.location_id) { toast.error('Selecciona una locación'); return; }
    if (!form.client_id) { toast.error('Selecciona un cliente'); return; }
    if (!form.project_id) { toast.error('Selecciona un proyecto'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        client_id: form.client_id,
        project_id: form.project_id,
        location_id: form.location_id,
        event_type: form.event_type,
        projected_risk_level: form.risk_level,
        dates: Object.fromEntries(Object.entries(form.dates).filter(([, v]) => v)),
        expected_attendance: form.expected_capacity ? parseInt(form.expected_capacity) : 0,
        crowd_management: form.crowd_management,
        audience_profile: { age_ranges: form.age_ranges, audience_types: form.audience_types },
        assigned_leader_id: form.assigned_leader_id || null,
        assigned_levantador_id: form.assigned_levantador_id || null,
        assigned_user_ids: form.assigned_user_ids.filter((userId) => userId !== form.assigned_levantador_id),
        notes: form.notes || null,
      };
      if (isEdit) {
        await eventsAPI.update(id, payload);
        toast.success('Evento actualizado');
        navigate(`/dashboard/events/${id}`);
      } else {
        const r = await eventsAPI.create(payload);
        toast.success('Evento creado');
        navigate(`/dashboard/events/${r.data.id}`);
      }
    } catch (err) {
      // #bh8Tivum — el msg podía venir undefined (ej. 503 sin body JSON) y
      // JSON.stringify(undefined) → undefined, que luego crasheaba en .slice
      // dejando al usuario sin feedback. Ahora siempre damos un mensaje útil.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (typeof detail === 'string' && detail) {
        msg = detail;
      } else if (detail) {
        msg = 'No se pudo guardar el evento. Revisa los datos e inténtalo de nuevo.';
      } else if (status === 503) {
        msg = 'El servidor no respondió (503). Reintenta en unos segundos.';
      } else if (status) {
        msg = `Error ${status} al guardar el evento.`;
      } else {
        msg = err?.message || 'Error al guardar el evento.';
      }
      toast.error(msg);
    } finally { setSaving(false); }
  };

  // #IncludeAdminsAsLeaders — super_admin también puede ser líder de
  // proyecto. Antes solo aparecían usuarios con role=lider_proyecto.
  // #LeaderDropdownFix — backend usa status='activo' además de is_active;
  // ambos checks para evitar dropdowns vacíos si is_active no existe.
  const isActiveUser = (u) =>
    u.is_active !== false && u.status !== 'inactivo' && u.status !== 'archivado';
  const leaders = users.filter(u => ['super_admin', 'lider_proyecto'].includes(u.role) && isActiveUser(u));
  // #IncludeAdminsAsLevantadores — admins (super_admin, lider) también
  // pueden ser levantadores asignados al evento.
  const levantadores = users.filter(u => ['super_admin', 'lider_proyecto', 'levantador'].includes(u.role) && isActiveUser(u));
  // #LeaderDropdownFix — helper para label robusto
  const userLabel = (u) => u.full_name || u.name || (`${u.last_name || ''} ${u.first_name || ''}`.trim()) || u.email || u.id;
  const dateErr = form.dates.event_start && form.dates.event_end ? validateDates() : null;
  const capacityWarn = selectedLocation?.max_capacity && form.expected_capacity
    && parseInt(form.expected_capacity) > selectedLocation.max_capacity;

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="event-form">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/dashboard/events')} className="soem-btn-secondary p-2" aria-label="Volver a eventos"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">{isEdit ? 'Editar Evento' : 'Nuevo Evento'}</h1>
          <p className="text-slate-500 text-sm mt-0.5">Define los detalles del evento de seguridad</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* S1: Info del evento */}
        <div className="soem-card p-6 space-y-5">
          <SectionTitle n="1">Información del Evento</SectionTitle>
          <div>
            <label htmlFor="f-nombre-del-evento" className="soem-label block mb-1.5">Nombre del evento <span className="text-red-500">*</span></label>
            <input id="f-nombre-del-evento" className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} required data-testid="evt-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-promotor-cliente" className="soem-label block mb-1.5">Promotor / Cliente <span className="text-red-500">*</span></label>
              <select id="f-promotor-cliente" className={inputCls} value={form.client_id} onChange={e => changeClient(e.target.value)} required>
                <option value="">— Seleccionar cliente —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="f-proyecto" className="soem-label block mb-1.5">Proyecto <span className="text-red-500">*</span></label>
              <select id="f-proyecto" className={inputCls} value={form.project_id} onChange={e => set('project_id', e.target.value)} required data-testid="evt-project">
                <option value="">— Seleccionar proyecto —</option>
                {projects.filter(p => !form.client_id || p.client_id === form.client_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="f-tipo-de-evento" className="soem-label block mb-1.5">Tipo de evento</label>
              <select id="f-tipo-de-evento" className={inputCls} value={form.event_type} onChange={e => set('event_type', e.target.value)}>
                {Object.entries(EVENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="f-nivel-de-riesgo-proyectado" className="soem-label block mb-2">Nivel de Riesgo Proyectado</label>
            <ToggleBtns
              options={[
                { value: 'bajo', label: '🟢 BAJO', style: 'bg-green-700' },
                { value: 'medio', label: '🟡 MEDIO', style: 'bg-yellow-500' },
                { value: 'alto', label: '🔴 ALTO', style: 'bg-red-600' },
              ]}
              value={form.risk_level} onChange={v => set('risk_level', v)}
            />
            <p className="text-xs text-slate-400 mt-1.5">Basado en el perfil del artista o la audiencia esperada</p>
          </div>
        </div>

        {/* S2: Fechas */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="2">Fechas y Tiempos</SectionTitle>
          {/* #AutoCalcDates — hint para que el usuario sepa que al fijar
              el montaje se autollena el resto, pero puede editarlas. */}
          <p className="text-xs text-slate-500 -mt-2">
            Al fijar el <strong>inicio de montaje</strong> se pre-llenan automáticamente:
            apertura +2 días · inicio +3 días · fin +4 días · desmontaje +6 días.
            Puedes ajustarlas manualmente.
          </p>
          {dateErr && (
            <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 flex items-center gap-2 text-orange-800 text-sm">
              <AlertTriangle size={15} /> {dateErr}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'setup_start', label: 'Inicio de montaje' },
              { key: 'doors_open', label: 'Apertura de puertas' },
              { key: 'event_start', label: 'Inicio del evento', req: true },
              { key: 'event_end', label: 'Fin del evento', req: true },
              { key: 'teardown_date', label: 'Fecha de desmontaje' },
            ].map(({ key, label, req }) => (
              <div key={key}>
                <label htmlFor={`f-date-${key}`} className="soem-label block mb-1.5">{label}{req && <span className="text-red-500"> *</span>}</label>
                <input id={`f-date-${key}`} type="datetime-local" className={inputCls}
                  value={form.dates[key]}
                  onChange={e => {
                    // Sólo el campo setup_start dispara el auto-calc en
                    // cascada. El resto usa setDate normal para que el
                    // usuario pueda ajustarlos sin efectos colaterales.
                    if (key === 'setup_start') {
                      setSetupStartAndDerive(e.target.value);
                    } else {
                      setDate(key, e.target.value);
                    }
                  }}
                  required={req} data-testid={`date-${key}`} />
              </div>
            ))}
          </div>
        </div>

        {/* S3: Locación */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="3">Locación</SectionTitle>
          {!form.client_id && <p className="text-sm text-slate-600">Selecciona el cliente para ver sus locaciones. Por ahora solo se muestran venues sin cliente asignado.</p>}
          {locationNotice && <p role="status" className="text-sm text-amber-700">{locationNotice}</p>}
          {locationError && <div role="alert" className="text-sm text-red-700">{locationError} <button type="button" onClick={() => setLocationRetry(n => n + 1)}>Reintentar</button></div>}
          <div className="relative">
            <label htmlFor="f-buscar-locación" className="soem-label block mb-1.5">Buscar locación <span className="text-red-500">*</span></label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input id="f-buscar-locación" className={`${inputCls} pl-9`} placeholder="Escribir nombre de la locación..."
                value={locationSearch} onChange={e => { setLocationSearch(e.target.value); }}
                data-testid="loc-search" />
            </div>
            {searchingLoc && <p className="text-xs text-slate-500">Buscando locaciones…</p>}
            {locationResults.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 max-h-48 overflow-y-auto">
                {locationResults.map(loc => (
                  <div key={loc.id} className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-slate-50 last:border-0"
                    onClick={() => { set('location_id', loc.id); setLocationNotice(''); setSelectedLocation(loc); setLocationSearch(''); setLocationResults([]); }}>
                    <p className="font-semibold text-soem-navy text-sm">{loc.name}</p>
                    <p className="text-xs text-slate-400">{loc.address?.city}</p>
                    <p className="text-xs text-slate-500">{locationClientLabel(loc, clients)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedLocation && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
              <p className="font-semibold text-soem-navy text-sm">{selectedLocation.name}</p>
              <p className="text-xs text-slate-500">{selectedLocation.address?.city}</p>
            </div>
          )}
          {!form.location_id && !locationSearch && (
            <p className="text-xs text-slate-400">Solo se muestran locaciones activas. <a href="/dashboard/locations/new" target="_blank" className="text-soem-primary hover:underline">+ Crear nueva locación</a></p>
          )}
        </div>

        {/* S4: Capacidad y audiencia */}
        <div className="soem-card p-6 space-y-5">
          <SectionTitle n="4">Capacidad y Audiencia</SectionTitle>
          <div>
            <label htmlFor="f-aforo-esperado" className="soem-label block mb-1.5 flex items-center gap-1">Aforo esperado</label>
            <input id="f-aforo-esperado" type="number" min="0" className={inputCls} value={form.expected_capacity}
              onChange={e => set('expected_capacity', e.target.value)} placeholder="0" data-testid="capacity" />
            {capacityWarn && (
              <p className="text-orange-600 text-xs mt-1 flex items-center gap-1">
                <AlertTriangle size={12} /> El aforo esperado supera la capacidad máxima de la locación ({selectedLocation.max_capacity.toLocaleString()})
              </p>
            )}
          </div>
          <div>
            <label htmlFor="f-manejo-de-multitudes" className="soem-label block mb-2">Manejo de multitudes</label>
            <ToggleBtns
              options={[
                { value: 'parados', label: '👥 Parados', style: 'bg-soem-navy' },
                { value: 'sentados', label: '💺 Sentados', style: 'bg-soem-navy' },
                { value: 'hibrido', label: '🔀 Híbrido', style: 'bg-soem-navy' },
              ]}
              value={form.crowd_management} onChange={v => set('crowd_management', v)}
            />
          </div>
          <div>
            {/* Encabezado de grupo, no de un checkbox individual — <p>, no <label for>. */}
            <p className="soem-label mb-2">Rangos de edad de la audiencia</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {AGE_RANGES.map(r => (
                // #M1 — el label tenía htmlFor="f-field" apuntando a un id
                // que no existe en ningún lado; el navegador no cae de
                // vuelta al checkbox anidado cuando `for` no resuelve, así
                // que clicks fuera del cuadrito de 13px no hacían nada.
                // El checkbox anidado ya asocia el label sin necesidad de
                // htmlFor — se quita, y cada opción recibe un id propio
                // en vez de compartir uno duplicado entre todas.
                <label key={r} className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer ${form.age_ranges.includes(r) ? 'border-soem-primary bg-blue-50' : 'border-slate-200'}`}>
                  <input id={`f-age-range-${r}`} type="checkbox" checked={form.age_ranges.includes(r)}
                    onChange={() => set('age_ranges', toggleArr(form.age_ranges, r))} />
                  <span className="text-xs font-medium">{r}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="soem-label mb-2">Tipo de audiencia</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {AUDIENCE_TYPES.map(t => (
                // #M1 — mismo bug que arriba (label con htmlFor roto).
                <label key={t.id} className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer ${form.audience_types.includes(t.id) ? 'border-soem-primary bg-blue-50' : 'border-slate-200'}`}>
                  <input id={`f-audience-type-${t.id}`} type="checkbox" checked={form.audience_types.includes(t.id)}
                    onChange={() => set('audience_types', toggleArr(form.audience_types, t.id))} />
                  <span className="text-xs font-medium">{t.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* S5: Asignaciones */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="5">Asignaciones</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-líder-de-proyecto" className="soem-label block mb-1.5">Líder de Proyecto</label>
              <select id="f-líder-de-proyecto" className={inputCls} value={form.assigned_leader_id} onChange={e => set('assigned_leader_id', e.target.value)}>
                <option value="">— Sin asignar —</option>
                {leaders.map(u => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="f-levantador" className="soem-label block mb-1.5">Levantador</label>
              <select id="f-levantador" className={inputCls} value={form.assigned_levantador_id}
                onChange={e => setForm(changePrincipal(form, e.target.value))}>
                <option value="">— Sin asignar —</option>
                {levantadores.map(u => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* #Fase3-G3 — El principal no se agrega automáticamente al equipo adicional. */}
        <TeamSelector users={users} value={form.assigned_user_ids} principalId={form.assigned_levantador_id}
          onChange={(ids) => set('assigned_user_ids', ids)} />

        {/* S6: Notas */}
        <div className="soem-card p-6">
          <SectionTitle n="6">Notas Generales</SectionTitle>
          <textarea className={`${inputCls} resize-none`} rows={4}
            placeholder="Observaciones, requerimientos especiales..."
            value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button type="submit" disabled={saving}
            className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-event-btn">
            <Save size={15} /> {saving ? 'Guardando...' : (isEdit ? 'Actualizar Evento' : 'Guardar Evento')}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/events')}
            className="soem-btn-secondary px-8 py-3 text-sm">Cancelar</button>
        </div>
      </form>
    </div>
  );
}
